import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ServicesPage = () => {
  const services = [
    {
      icon: 'ri-cpu-line',
      title: 'Import & Wholesale of Electronic Components',
      description: 'Comprehensive supply of high-quality electronic components from certified global manufacturers with strict quality control and competitive pricing.',
      features: [
        'Microelectronics and integrated circuits',
        'Sensors, transducers, and measurement devices',
        'Cables, connectors, and wiring systems',
        'Modules, boards, and development kits',
        'Passive components (resistors, capacitors, inductors)',
        'Active components (transistors, diodes, ICs)',
        'Power supplies and voltage regulators',
        'Display modules and LED components',
      ],
      benefits: [
        'Direct relationships with manufacturers',
        'Certified quality assurance',
        'Competitive wholesale pricing',
        'Large inventory availability',
      ],
      image: 'https://public.readdy.ai/ai/img_res/96ec73a450c30ff18bc7b108058cdf74.jpg',
    },
    {
      icon: 'ri-global-line',
      title: 'Distribution of Equipment & Components (EU & Ukraine)',
      description: 'Efficient B2B distribution network serving enterprises and system integrators across European and Ukrainian markets with fast processing and reliable delivery.',
      features: [
        'Fast order processing and fulfillment',
        'Wide warehouse network across regions',
        'B2B deliveries to enterprises',
        'Supply to system integrators',
        'Flexible ordering and payment terms',
        'Technical documentation support',
        'Product certification assistance',
        'After-sales technical support',
      ],
      benefits: [
        'Strategic warehouse locations',
        'Express delivery options',
        'Dedicated account managers',
        'Volume discount programs',
      ],
      image: 'https://readdy.ai/api/search-image?query=modern%20warehouse%20distribution%20center%20with%20organized%20inventory%20shelves%20and%20logistics%20operations%20clean%20professional%20environment%20with%20workers%20managing%20electronic%20components%20efficient%20supply%20chain%20management%20blue%20and%20white%20corporate%20setting&width=800&height=600&seq=service-detail-002&orientation=landscape',
    },
    {
      icon: 'ri-tools-line',
      title: 'Technical Spare Parts Trade',
      description: 'Reliable supply of mechanical, electrical, and industrial spare parts for technical systems and machinery with comprehensive replacement component solutions.',
      features: [
        'Mechanical spare parts and assemblies',
        'Electrical components and systems',
        'Industrial automation parts',
        'Replacement components for equipment',
        'Technical system components',
        'Machinery parts and accessories',
        'Maintenance and repair supplies',
        'Custom part sourcing services',
      ],
      benefits: [
        'Quick availability and delivery',
        'Cross-reference identification',
        'Quality guaranteed parts',
        'Technical specification matching',
      ],
      image: 'https://readdy.ai/api/search-image?query=collection%20of%20precision%20mechanical%20and%20electrical%20spare%20parts%20industrial%20components%20gears%20motors%20and%20technical%20equipment%20parts%20arranged%20systematically%20on%20clean%20surface%20professional%20product%20photography%20with%20metallic%20finish%20and%20technical%20details&width=800&height=600&seq=service-detail-003&orientation=landscape',
    },
    {
      icon: 'ri-truck-line',
      title: 'International Logistics & Brokerage Services',
      description: 'Complete cargo coordination, customs support, and delivery route optimization with full-cycle accompaniment of international shipments across multiple transport modes.',
      features: [
        'Cargo coordination and management',
        'Customs clearance support',
        'Delivery route optimization',
        'Full-cycle shipment accompaniment',
        'Land, air, and sea transport',
        'Documentation handling',
        'Insurance coordination',
        'Real-time tracking and updates',
      ],
      benefits: [
        'Multi-modal transport options',
        'Experienced customs brokers',
        'Cost-effective routing',
        'End-to-end visibility',
      ],
      image: 'https://readdy.ai/api/search-image?query=international%20cargo%20logistics%20operations%20with%20trucks%20shipping%20containers%20and%20freight%20at%20modern%20terminal%20professional%20supply%20chain%20management%20organized%20workflow%20clean%20corporate%20environment%20with%20blue%20sky%20and%20efficient%20operations&width=800&height=600&seq=service-detail-004&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative pt-32 pb-24 px-6 lg:px-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
            <i className="ri-service-line text-white"></i>
            <span className="text-sm text-white font-semibold">Our Services</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Comprehensive <strong>Technical Solutions</strong>
          </h1>

          <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
            From electronic components import to international logistics, we provide end-to-end solutions tailored to your business needs.
          </p>
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto space-y-32">
          {services.map((service, index) => (
            <div
              key={index}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
                  <i className={`${service.icon} text-blue-600`}></i>
                  <span className="text-sm text-blue-600 font-semibold">Service {index + 1}</span>
                </div>

                <h2 className="text-4xl font-bold text-slate-900 mb-6">{service.title}</h2>

                <p className="text-lg text-slate-600 mb-8 leading-relaxed">{service.description}</p>

                <div className="mb-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Key Features:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Benefits:</h3>
                  <div className="space-y-2">
                    {service.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-md flex items-center justify-center flex-shrink-0">
                          <i className="ri-star-fill text-white text-xs"></i>
                        </div>
                        <span className="text-slate-700 font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <div className="absolute -top-8 -right-8 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="relative rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 lg:px-12 bg-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Ready to Get <strong>Started?</strong>
          </h2>
          <p className="text-xl text-slate-600 mb-12">
            Contact us today to discuss how our services can support your business growth.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-lg font-bold rounded-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-message-3-line mr-2"></i>
            Request a Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
